import { useCallback, useEffect, useState } from 'react';
import api from '../../hooks/useApi';
import FilePreviewModal, { previewKind } from './FilePreviewModal';

// The Files tab on a SOW or a bid: the record's SharePoint folder, browsed in
// Ops. Folders open in place (with a path back up); a file opens in the
// in-app viewer, fetched through /api/sharepoint-files, which only serves
// files inside this record's own folder (src/api/routes/sharepointFiles.js).

const fmtSize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const iconFor = (item) => {
  if (item.is_folder) return '📁';
  const k = previewKind(item.name);
  return k === 'pdf' ? '📕' : k === 'docx' ? '📘' : k === 'image' ? '🖼️' : '📄';
};

export default function SharePointFiles({ kind, id, emptyHint }) {
  const [trail, setTrail] = useState([]);      // [{ id, name }] below the record folder
  const [state, setState] = useState(null);    // the server's answer
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const current = trail.length ? trail[trail.length - 1].id : null;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = current ? `?folder=${encodeURIComponent(current)}` : '';
      setState(await api.get(`sharepoint-files/${kind}/${encodeURIComponent(id)}${qs}`));
    } catch (e) {
      setError(e.message || 'SharePoint could not be reached.');
    } finally { setLoading(false); }
  }, [kind, id, current]);

  useEffect(() => { load(); }, [load]);

  if (state && state.linked === false) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-[11px] text-gray-500">
        {emptyHint || 'No SharePoint folder is linked to this record yet.'}
      </div>
    );
  }

  const items = state?.items || [];
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-1 flex-wrap text-[11px]">
          <button onClick={() => setTrail([])} className={`font-semibold ${trail.length ? 'text-blue-700 hover:underline' : 'text-navy'}`}>
            {state?.root?.name || 'SharePoint folder'}
          </button>
          {trail.map((t, i) => (
            <span key={t.id} className="flex items-center gap-1">
              <span className="text-gray-400">/</span>
              <button onClick={() => setTrail(trail.slice(0, i + 1))}
                className={`font-semibold ${i < trail.length - 1 ? 'text-blue-700 hover:underline' : 'text-navy'}`}>{t.name}</button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} disabled={loading} className="text-[10px] text-gray-500 hover:text-navy disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          {(state?.folder?.web_url || state?.root?.web_url) && (
            <a href={state.folder?.web_url || state.root.web_url} target="_blank" rel="noopener noreferrer"
              className="text-[10px] font-semibold text-navy hover:text-gold">Open in SharePoint →</a>
          )}
        </div>
      </div>

      {error ? (
        <div className="px-3 py-4 text-[11px] text-red-700">{error}</div>
      ) : loading && !state ? (
        <div className="px-3 py-6 text-center text-[11px] text-gray-500">Loading the folder…</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-6 text-center text-[11px] text-gray-500">This folder is empty.</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[9px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
              <th className="px-3 py-1.5 font-semibold">Name</th>
              <th className="px-3 py-1.5 font-semibold hidden sm:table-cell">Modified</th>
              <th className="px-3 py-1.5 font-semibold hidden md:table-cell">By</th>
              <th className="px-3 py-1.5 font-semibold text-right">Size</th>
            </tr>
          </thead>
          <tbody>
            {trail.length > 0 && (
              <tr className="border-b border-gray-100 hover:bg-gray-50">
                <td colSpan={4} className="px-3 py-1.5">
                  <button onClick={() => setTrail(trail.slice(0, -1))} className="text-blue-700 hover:underline">↩ Up a folder</button>
                </td>
              </tr>
            )}
            {items.map(it => (
              <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-1.5">
                  <button
                    onClick={() => it.is_folder ? setTrail([...trail, { id: it.id, name: it.name }]) : setViewing(it)}
                    className="text-left text-navy hover:text-blue-700 hover:underline">
                    <span className="mr-1.5">{iconFor(it)}</span>{it.name}
                  </button>
                  {it.is_folder && it.child_count != null && <span className="ml-1.5 text-[9px] text-gray-400">{it.child_count} item{it.child_count === 1 ? '' : 's'}</span>}
                </td>
                <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap hidden sm:table-cell">{fmtDate(it.modified_at)}</td>
                <td className="px-3 py-1.5 text-gray-500 hidden md:table-cell">{it.modified_by || ''}</td>
                <td className="px-3 py-1.5 text-gray-500 text-right whitespace-nowrap">{it.is_folder ? '' : fmtSize(it.size)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {state?.truncated && <div className="px-3 py-1.5 text-[10px] text-amber-700">Showing the first 2,000 items. Open the folder in SharePoint to see the rest.</div>}

      {viewing && (
        <FilePreviewModal
          url={`/api/sharepoint-files/${kind}/${encodeURIComponent(id)}/file/${encodeURIComponent(viewing.id)}`}
          filename={viewing.name} title={viewing.name} openUrl={viewing.web_url}
          onClose={() => setViewing(null)} />
      )}
    </div>
  );
}
